// What we build: the three commercial products, the phone app that comes with them, and what we
// give away. Each product has an anchor (#workflows, #broker, #cloud) the articles link to.
import { ArrowRight, Check } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router";

import { COMPANION, PRODUCTS } from "../content/products";
import { useTitle } from "../ui/hooks";
import { PageHero, SectionHead } from "../ui/Section";
import { TOOLS } from "./tools/catalogue";

export function Products() {
  useTitle("Products");
  return (
    <>
      <PageHero kicker="§ Products" title={<>Built on a platform that runs agents for real.</>}>
        OCS began as a way to supervise AI agents. It now runs real engineering work every day, and its parts are becoming products of their own. Here is what exists, and what is coming.
      </PageHero>

      {PRODUCTS.map((p, i) => (
        <section key={p.id} id={p.id} className={clsx("scroll-mt-20 border-b border-line", i % 2 === 1 && "bg-paper2/50")}>
          <div className="wrap grid gap-10 py-16 sm:py-20 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-paper">
                  <p.Icon weight="duotone" className="h-6 w-6" aria-hidden />
                </span>
                <span className="chip">{p.status}</span>
              </div>
              <h2 className="display mt-6 text-5xl sm:text-6xl">
                {p.name}
                {p.codename ? <span className="ms-3 align-middle font-mono text-base font-medium tracking-normal text-ink3">codename “{p.codename}”</span> : null}
              </h2>
              <p className="mt-3 font-serif text-2xl italic text-ink2">{p.tagline}</p>
              <p className="mt-5 max-w-xl text-[16.5px] leading-relaxed text-ink2">{p.body}</p>
              <Link to={`/charter?interest=${p.interest}#sign`} className="btn-ink mt-8">
                {p.status.includes("development") ? "Join the early-access list" : "Ask for early access"} <ArrowRight weight="bold" className="h-4 w-4" />
              </Link>
            </div>
            <ul className="card divide-y divide-line self-start">
              {p.features.map((f) => (
                <li key={f} className="flex gap-3 px-5 py-4 text-[15.5px]">
                  <Check weight="bold" className="mt-1 h-4 w-4 shrink-0 text-signal" aria-hidden /> {f}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <section className="wrap py-16">
        <div className="card flex flex-col gap-6 p-8 sm:flex-row sm:items-center">
          <COMPANION.Icon weight="duotone" className="h-14 w-14 shrink-0" aria-hidden />
          <div>
            <h2 className="h3">{COMPANION.name}</h2>
            <p className="mt-1 max-w-2xl text-[15.5px] text-ink2">{COMPANION.body}</p>
          </div>
        </div>
      </section>

      <section className="wrap py-10">
        <SectionHead kicker="§ Free, for everyone" title="And the things we give away.">
          The community's tools are free to use, with no account. They are how we make the Charter practical.
        </SectionHead>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TOOLS.map((t) => (
            <Link key={t.to} to={t.to} className="card group p-5 hover:border-ink">
              <t.Icon weight="duotone" className="h-7 w-7" aria-hidden />
              <p className="mt-4 font-display text-lg font-bold tracking-tight group-hover:text-signal">{t.name}</p>
              <p className="mt-1 text-[14px] text-ink2">{t.line}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
